"use client";
import { FilterOptions, useSearch } from "@/context/SearchContext";
import { useLocale } from "@/context/LocaleContext";
import Image from "next/image";
import { I18nData } from "@/types/i18n";
import { useEffect, useRef, useState } from "react";
import FilterIcon from "./images/filter.png";
import { Branding } from "@/types/branding";
import { FilterDropdownOptions } from "./filter-dropdown-options";
import { ColumnKey, resolveColumnOrder } from "./column-defs";

interface TableHeaderProps {
  branding?: Branding | null;
  i18nData?: I18nData;
}

export default function TableHeader({ branding, i18nData }: TableHeaderProps) {
  const { toggleFilter, filterState, currentSearchTerm } = useSearch();
  const { locale } = useLocale();

  // Show filters globally only if there's a search term
  const showFiltersGlobally = !!currentSearchTerm;
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  // Trigger buttons per column, so focus can return to the one that opened the dropdown
  const filterButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    if (!showFiltersGlobally) {
      setOpenFilter(null);
    }
  }, [showFiltersGlobally]);

  const toggleDropdown = (filterName: string) => {
    setOpenFilter((prev) => (prev === filterName ? null : filterName));
  };

  const closeDropdown = () => {
    // Accessibility: the focused checkbox unmounts with the dropdown, which would drop
    // focus to <body> and make screen readers re-read the whole page. Returning focus
    // to the trigger button lets the results live region be announced instead.
    if (openFilter) filterButtonRefs.current[openFilter]?.focus();
    setOpenFilter(null);
  };

  const handleOptionChange = (key: keyof FilterOptions, value: string) => {
    toggleFilter(key, value);
    closeDropdown();
  };

  // Get display name for filter option from i18n data, fallback to original value
  const getFilterOptionDisplayName = (
    filterKey: keyof FilterOptions,
    optionValue: string,
  ): string => {
    const localeData = i18nData?.[locale];

    switch (filterKey) {
      case "payment":
        return localeData?.payments?.[optionValue]?.name || optionValue;
      case "licenses":
        return localeData?.licenses?.[optionValue]?.name || optionValue;
      case "publishing_model":
        return (
          localeData?.publishing_models?.[optionValue]?.name || optionValue
        );
      case "quality_score": {
        const colonIdx = optionValue.indexOf(":");
        const scoreValue = colonIdx >= 0 ? optionValue.slice(colonIdx + 1) : optionValue;
        return scoreValue === "null"
          ? localeData?.quality_labels_unscored || "Unscored"
          : (localeData?.[
              `quality_labels_level${scoreValue}` as keyof typeof localeData
            ] as string) || (/^[0-9A-Z]$/.test(scoreValue) ? `Level ${scoreValue}` : scoreValue);
      }
      default:
        return optionValue;
    }
  };

  const qualityList = branding?.extensions?.quality_lists;
  const hasQualityExtension = Boolean(qualityList && qualityList.length > 0);

  type HeaderMeta = {
    id: string;
    key: keyof FilterOptions;
    label: string;
    options: string[];
    hasFilterOptions: boolean;
    sections?: string[];
  };

  const columnHeaderMeta: Record<ColumnKey, HeaderMeta> = {
    imprint: {
      id: "imprint",
      key: "imprint",
      label: i18nData?.[locale]?.imprint || "Imprint",
      options: filterState.available.imprint || [],
      hasFilterOptions: !!(
        filterState.available.imprint &&
        filterState.available.imprint.length > 0
      ),
    },
    payment: {
      id: "payment",
      key: "payment",
      label:
        i18nData?.[locale]?.payment_title ??
        branding?.payment_title ??
        "Who Pays",
      options: filterState.available.payment || [],
      hasFilterOptions: !!(
        filterState.available.payment &&
        filterState.available.payment.length > 0
      ),
    },
    license: {
      id: "license",
      key: "licenses",
      label: i18nData?.[locale]?.license_options || "License options",
      options: filterState.available.licenses || [],
      hasFilterOptions: !!(
        filterState.available.licenses &&
        filterState.available.licenses.length > 0
      ),
    },
    publishingModel: {
      id: "publishingModel",
      key: "publishing_model",
      label: i18nData?.[locale]?.publishing_model || "Publishing model",
      options: filterState.available.publishing_model || [],
      hasFilterOptions: !!(
        filterState.available.publishing_model &&
        filterState.available.publishing_model.length > 0
      ),
    },
    quality: (() => {
      const allQualityOptions = filterState.available.quality_score || [];
      const filteredQualityOptions = hasQualityExtension
        ? allQualityOptions.filter((opt) =>
            qualityList!.some((list) =>
              opt.startsWith(`${list.toLowerCase()}:`),
            ),
          )
        : [];
      return {
        id: "quality",
        key: "quality_score",
        label: i18nData?.[locale]?.quality_title || "Quality",
        options: filteredQualityOptions,
        hasFilterOptions: filteredQualityOptions.length > 0,
        sections:
          hasQualityExtension &&
          Array.isArray(qualityList) &&
          qualityList.length > 1
            ? qualityList
            : undefined,
      };
    })(),
  };

  const columnHeaders = resolveColumnOrder(
    branding?.column_order,
    hasQualityExtension,
  ).map((key) => columnHeaderMeta[key]);

  return (
    <thead className="table-header">
      <tr>
        <th id="journals" scope="col">
          {i18nData?.[locale]?.journals || "Journals"}
        </th>
        {columnHeaders.map(
          ({ id, key, label, options, hasFilterOptions, sections }) => {
            // Only show filter functionality if globally enabled and this column has options
            const showFilterForColumn = showFiltersGlobally && hasFilterOptions;

            return (
              <th key={id} id={id} scope="col">
                {showFilterForColumn ? (
                  <button
                    type="button"
                    className="dropdown-label clickable cursor-pointer flex items-center justify-between md:justify-center min-h-[44px] w-full"
                    ref={(el) => {
                      filterButtonRefs.current[id] = el;
                    }}
                    onClick={() => toggleDropdown(id)}
                    aria-label={label}
                    aria-expanded={openFilter === id}
                    aria-controls={`${id}Select`}
                  >
                    <span>{label}</span>
                    <Image
                      className="dropdown-icon dropdown-icon-element"
                      alt=""
                      src={FilterIcon}
                      width={25}
                      height={25}
                    />
                  </button>
                ) : (
                  <div className="dropdown-label flex items-center justify-between md:justify-center min-h-[44px]">
                    <span>{label}</span>
                  </div>
                )}
                {showFilterForColumn && openFilter === id && (
                  <ul
                    id={`${id}Select`}
                    className="selectDropdown"
                    onKeyDown={(e) => {
                      if (e.key === "Escape") closeDropdown();
                    }}
                  >
                    <div className="mb-2">
                      <span>
                        {i18nData?.[locale]?.filter_dropdown_accessibility_info}
                      </span>
                    </div>
                    <FilterDropdownOptions
                      id={id}
                      filterKey={key}
                      options={options}
                      sections={sections}
                      selectedValues={filterState.selected[key] || []}
                      onOptionChange={handleOptionChange}
                      getDisplayName={getFilterOptionDisplayName}
                    />
                  </ul>
                )}
              </th>
            );
          },
        )}
      </tr>
    </thead>
  );
}
